import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectBillingPrefs } from '@/store/slices/userPreferences';

import { Button, Callout, Icon, Panel, Skeleton, Text, type IconName } from '../../components';
import { useRefetchOnVisible } from '../../hooks/useRefetchOnVisible';
import { COLORS, STATUS_COLORS } from '../../theme/tokens';
import { dateLocale } from '../../utils/locale';
import { saveBlob } from '../creatives/download-asset';
import { useBrandSettled } from '../website/useBrandSettled';
import {
  esignLanguage,
  SILENT_CODES,
  type ContractService,
  type ContractStatus,
  type ContractStatusValue,
} from './api/contract';
import { contractService } from './api/service';
import { ContractDialog, SecuredPicture, StepCards } from './components';
import { fallbackFilename } from './download';
import { contractErrorText, contractNoticeTone, takeContractError } from './errors';
import { startBody, type NameValues } from './form';
import { prefillFrom, restartPrefill } from './prefill';
import * as S from './secure-brand.styles';
import { initState, reducer, shownStatus, showsSkeleton, type ActionKind, type Dialog } from './state';
import { rememberBrandSecured } from './useBrandSecured';

export type SecureBrandSectionProps = {
  /** CT injects a fixture transport; the app uses spark-esign over http (`contractService`). */
  service?: ContractService;
};

const ICON_COLOR: Record<ContractStatusValue, string> = {
  not_secured: COLORS.textMuted,
  sent: COLORS.text,
  secured: STATUS_COLORS.positive,
};

/** The form as a Start. One object, so the popup sees the same dialog from one render to the next. */
const START_FORM: Dialog = { kind: 'form', mode: 'start' };

/**
 * Settings > Secure your brand, mounted at `/account/settings/secure-brand` under the
 * `secure-brand` feature flag (the Settings dispatcher provides the chrome). The owner signs the
 * License Holder Declaration through DocuSign; this shows where the brand stands (Not secured,
 * Document sent, Secured) and the actions each state allows. The state machine is `state.ts`; this
 * wires it to the store, the contract service and the components.
 *
 * Actions: Start and Start over open the popup (`ContractDialog`), prefilled from the owner's Billing
 * details (Start over: from the names the service kept), and Continue sends; Resend sends again;
 * Download saves the signed copy. A send that lands closes the popup, and the step cards (`StepCards`)
 * are the confirmation: Document sent, step 1 done ("Contract sent", "again" after a Resend), signing
 * lit. One action at a time, and every answer is stamped with the brand it was made for. A refusal
 * shows in the popup while the form is open, otherwise under the cards; the silent ones close the popup
 * and read again instead.
 *
 * Reads: on mount, on every brand switch, when the tab or the window comes back, and on Retry (no
 * websocket, no polling). One read per brand at a time: a return can fire both `visibilitychange`
 * and `focus`, so a read already in flight for the brand is not started again, while a read for a
 * new brand always starts (the previous brand's answer is dropped by the reducer). Loading follows
 * the area's rules: the skeleton until the brand list settles and the first read answers, a refetch
 * keeps the status on screen, and only a first read that fails shows the Load failed notice. With no
 * brand, no request is made and Start is disabled with the domain line.
 *
 * `data-status` on the section is the status on screen, `none` while there is none (the skeleton, a
 * failed first read, no brand).
 */
export const SecureBrandSection = ({ service = contractService }: SecureBrandSectionProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const displayBrand = useAppSelector((s) => s.casinoInstance.displayBrand);
  const brandId = displayBrand?.id ?? null;
  const brandSettled = useBrandSettled();
  const billing = useAppSelector(selectBillingPrefs);
  const prefsLoaded = useAppSelector((s) => s.userPreferences.loaded);
  // Lazy init: the first paint is already loading for the brand, never the idle "no brand" layout.
  const [state, dispatch] = React.useReducer(reducer, brandId, initState);

  // Every read gets the next id, and the reducer lands only the newest read's answer. `inFlight` is the
  // read running now: mount and focus skip while it is for this brand (a read for brand B never waits
  // on brand A's), and `force` (the re-read after a failed action) starts one anyway, which replaces it.
  const reads = React.useRef(0);
  const inFlight = React.useRef<{ brandId: string; read: number } | null>(null);
  // The brand the reducer was last reset to.
  const synced = React.useRef(brandId);

  // A brand switch resets the section to the new brand (a no-op on the same id). The reset disowns every
  // read in flight, so the skip forgets them too: a read still running for the brand switched back to
  // (the re-read an action asked for before the switch) must not hold off that brand's read.
  React.useEffect(() => {
    if (synced.current !== brandId) {
      synced.current = brandId;
      inFlight.current = null;
    }
    dispatch({ type: 'brand', brandId });
  }, [brandId]);

  const load = React.useCallback(
    async (force = false): Promise<void> => {
      if (!brandId || (!force && inFlight.current?.brandId === brandId)) return;
      reads.current += 1;
      const read = reads.current;
      inFlight.current = { brandId, read };
      dispatch({ type: 'load-start', brandId, read });
      try {
        const status = await service.fetchStatus(brandId);
        dispatch({ type: 'load-ok', brandId, read, status });
        // The section's title (and the Settings row) say "secured" from the newest read's answer.
        if (reads.current === read) rememberBrandSecured(brandId, status.status === 'secured');
      } catch (rejection) {
        dispatch({ type: 'load-failed', brandId, read, error: takeContractError(rejection) });
      } finally {
        if (inFlight.current?.read === read) inFlight.current = null;
      }
    },
    [brandId, service],
  );

  // Reads the brand's status from spark-esign on mount and on every brand switch.
  React.useEffect(() => {
    void load();
  }, [load]);

  useRefetchOnVisible(() => void load(), brandId !== null);

  /** A date only, in the app language ("October 1, 2026" in English); empty when there is none. */
  const formatDate = (iso: string | null): string =>
    iso === null
      ? ''
      : new Date(iso).toLocaleDateString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' });

  const skeleton = showsSkeleton(state, brandId, brandSettled);
  const status = shownStatus(state.load);
  // No brand (the idle phase): the Not secured layout, blocked on the domain.
  const noBrand = state.load.kind === 'idle';
  const view: ContractStatusValue = status?.status ?? 'not_secured';
  const blocked = noBrand ? 'no_domain' : status?.blocked ?? null;
  const busy = state.busy !== null;
  const icon: IconName = view === 'sent' ? 'mail' : 'shield';
  const previousOutcome = view === 'not_secured' ? status?.previous_outcome ?? null : null;
  // An action's refusal shows here unless the form popup is open (it shows it inside).
  const noticeText = state.notice && state.dialog.kind !== 'form' ? contractErrorText(state.notice, t) : null;
  // A Start over form stays one only while a contract is pending. Once the read shows none (a new send
  // that failed after the void), the open form carries on as a Start: Continue sends Start, and the
  // popup drops the line about cancelling the current contract.
  const dialog: Dialog =
    state.dialog.kind === 'form' && state.dialog.mode === 'restart' && status?.status !== 'sent'
      ? START_FORM
      : state.dialog;

  // The popup's prefill: the Billing details once the preferences are in (until then, the table's
  // "no billing" row); Start over puts the names the service kept from the last attempt over them.
  const basePrefill = prefillFrom(prefsLoaded ? billing : null);
  const prefill =
    state.dialog.kind === 'form' && state.dialog.mode === 'restart' && status
      ? restartPrefill(status, prefsLoaded ? billing : null)
      : basePrefill;

  const onStart = (): void => {
    if (blocked || state.busy) return;
    dispatch({ type: 'open', mode: 'start' });
  };
  const onRestart = (): void => {
    if (blocked || state.busy) return;
    dispatch({ type: 'open', mode: 'restart' });
  };

  /**
   * One action at a time for the brand on screen, its answer stamped with that brand (an answer for a
   * brand switched away from is dropped by the reducer). A silent refusal closes the popup and reads
   * again: the current state says the rest (Decision 10). A failed Start over reads again on any failure,
   * a network one included: when the new send failed after the void, the brand is not secured now, so the
   * read shows it and the open form carries on as a Start. That read is forced: a read already in flight
   * was asked before the action, so its answer is dropped.
   */
  const run = async (kind: ActionKind, call: (id: string) => Promise<ContractStatus | null>): Promise<void> => {
    if (!brandId || state.busy) return;
    dispatch({ type: 'action-start', kind });
    try {
      const answer = await call(brandId);
      dispatch({ type: 'action-ok', kind, brandId, status: answer });
    } catch (rejection) {
      const error = takeContractError(rejection);
      dispatch({ type: 'action-failed', kind, brandId, error });
      if (kind === 'restart' || (error && SILENT_CODES.has(error.code))) void load(true);
    }
  };

  const onContinue = (values: NameValues): void => {
    const mode = dialog.kind === 'form' ? dialog.mode : 'start';
    const body = startBody(values, prefill.signatoryShown, esignLanguage(i18n.language));
    void run(mode, (id) => (mode === 'restart' ? service.restart(id, body) : service.start(id, body)));
  };
  const onResend = (): void => {
    if (blocked) return;
    void run('resend', (id) => service.resend(id));
  };
  // Download is never blocked. The service names the file; the fallback is the same name, built here.
  const onDownload = (): void => {
    void run('download', async (id) => {
      const doc = await service.download(id);
      saveBlob(doc.blob, doc.filename ?? fallbackFilename(displayBrand?.casino_data?.details));
      return null;
    });
  };
  // Cancel, Escape and the backdrop: the reducer ignores a close while a send is on its way.
  const onCancel = (): void => dispatch({ type: 'close' });

  // Why the buttons are disabled, under them in step 1's card. Secured has no blocked action.
  const blockedLine =
    view !== 'secured' && blocked !== null ? (
      <S.Blocked data-testid="secure-brand-blocked">
        {blocked === 'email_unverified' ? (
          <Text text="Verify your email to continue" />
        ) : (
          <Text text="Available once your domain is live." />
        )}
      </S.Blocked>
    ) : null;

  return (
    <>
      <Panel>
        <div data-testid="secure-brand-section" data-status={skeleton ? 'none' : status?.status ?? 'none'}>
          {skeleton ? (
            <S.Stack data-testid="secure-brand-skeleton">
              <Skeleton width={180} height={28} />
              <Skeleton block height={60} />
              <Skeleton block height={44} />
            </S.Stack>
          ) : state.load.kind === 'failed' ? (
            <div data-testid="secure-brand-load-failed">
              <Callout
                tone="danger"
                title={t('The contract status could not be loaded.')}
                text={t('Something went wrong. Please try again.')}
                action={<Button variant="outline" icon="refresh" label={t('Retry')} onClick={() => void load()} />}
              />
            </div>
          ) : (
            // Secured is a taller layout: its words centred down the left, the signed contract faded on the right.
            <S.Body>
              <S.Stack $secured={view === 'secured'}>
                <S.StatusLine data-testid="secure-brand-status" data-icon={icon}>
                  <Icon name={icon} color={ICON_COLOR[view]} />
                  {view === 'sent' ? (
                    <S.StatusLabel text="Document sent" />
                  ) : view === 'secured' ? (
                    <S.StatusLabel text="Secured" />
                  ) : (
                    <S.StatusLabel text="Not secured" />
                  )}
                </S.StatusLine>

                {status?.status === 'sent' ? (
                  <S.Intro
                    text="We sent the contract to {{email}} on {{date}}. Check your inbox."
                    placeholders={{ email: status.email, date: formatDate(status.sent_at) }}
                    tag="paragraph"
                  />
                ) : status?.status === 'secured' ? (
                  <S.Intro
                    text="Signed on {{date}}. Your brand is secured and everything is in order."
                    placeholders={{ date: formatDate(status.signed_at) }}
                    tag="paragraph"
                  />
                ) : (
                  <S.Intro
                    text="Secure your brand by signing the License Holder Declaration with DocuSign. In it, Starfall, the license holder, declares that your domain, your brand and your player base belong to you."
                    tag="paragraph"
                  />
                )}

                {previousOutcome !== null ? (
                  <div data-testid="secure-brand-previous-outcome">
                    {previousOutcome === 'expired' ? (
                      <S.Note text="Your previous contract expired." tag="paragraph" />
                    ) : previousOutcome === 'declined' ? (
                      <S.Note text="Your previous contract was declined." tag="paragraph" />
                    ) : (
                      <S.Note text="Your previous contract was cancelled." tag="paragraph" />
                    )}
                  </div>
                ) : null}

                {/* One StepCards for Not secured and Document sent, in one place in the tree, so a send that
                  lands turns the cards in place: step 1 done, signing lit. Step 1 holds the actions. */}
                {view !== 'secured' ? (
                  <StepCards
                    sent={view === 'sent' ? { again: state.sentAgain, sends: state.sends } : null}
                    action={
                      view === 'sent' ? (
                        <>
                          <Button
                            variant="outline"
                            icon="refresh"
                            label={t('Resend contract')}
                            disabled={blocked !== null || busy}
                            onClick={onResend}
                          />
                          <Button
                            variant="text"
                            label={t('Start over')}
                            disabled={blocked !== null || busy}
                            onClick={onRestart}
                          />
                          {blockedLine}
                        </>
                      ) : (
                        <>
                          <Button label={t('Start')} disabled={blocked !== null || busy} onClick={onStart} />
                          {blockedLine}
                        </>
                      )
                    }
                  />
                ) : (
                  <S.Actions>
                    {/* Download contract is never blocked. */}
                    <Button icon="download" label={t('Download contract')} disabled={busy} onClick={onDownload} />
                  </S.Actions>
                )}

                {noticeText !== null ? (
                  <div data-testid="secure-brand-notice">
                    <Callout tone={contractNoticeTone(state.notice?.error ?? null)} text={noticeText} />
                  </div>
                ) : null}
              </S.Stack>
              {view === 'secured' ? (
                <S.SecuredArt>
                  <SecuredPicture />
                </S.SecuredArt>
              ) : null}
            </S.Body>
          )}
        </div>
      </Panel>
      <ContractDialog
        dialog={dialog}
        prefill={prefill}
        sending={state.busy === 'start' || state.busy === 'restart'}
        notice={state.dialog.kind === 'form' ? state.notice : null}
        onCancel={onCancel}
        onContinue={onContinue}
      />
    </>
  );
};

export default SecureBrandSection;
